import {
  applyCastToMoveState,
  CAST_REJECT_TEXT,
  validateCast,
  type CastRejectReason,
} from '@jianghu/combat-engine';
import {
  getItem,
  getQuest,
  getSect,
  getSkill,
  LOOT_TABLES,
  MONSTERS,
  getZone,
  getZoneLayout,
  skillStepHits,
  STARTING_ZONE_ID,
  type ZoneDef,
  type ZoneLayout,
} from '@jianghu/game-data';
import { countInBag } from '@jianghu/game-rules';
import {
  ENTITY_STATE,
  type CastAction,
  type CharacterSelfDTO,
  type EntitySpawnDTO,
  type SelfSnapshot,
  type ServerMessage,
} from '@jianghu/protocol';
import {
  createMoveState,
  dist,
  HUMANOID_RADIUS,
  INPUT_DT,
  INTERACT_RANGE,
  INTERPOLATION_DELAY_MS,
  NavGrid,
  normalize,
  stepMovement,
  type MoveCommand,
  type MoveState,
} from '@jianghu/shared';
import * as THREE from 'three';
import { useSettings } from '../../stores/settings-store';
import { audio, type SfxName } from '../audio/audio';
import { CameraController } from '../camera/camera-controller';
import { EntityView } from '../entities/entity-view';
import { Input, type Action } from '../input/input';
import { NetClient } from '../net/net-client';
import { detectQuality, GameRenderer, PRESETS, type QualityLevel } from '../render/game-renderer';
import { gameUi, useGame, type HudTarget } from '../store';
import { MapRenderer, type MapMarker } from '../ui/minimap';
import { Overlay } from '../ui/overlay';
import { Vfx } from '../vfx/vfx';
import { buildWorld, type WorldVisuals } from '../world/world-builder';
import { playCastFx, type FxContext } from './skill-fx';

interface Pending {
  seq: number;
  cmd: MoveCommand;
  action?: CastAction;
}

interface Scheduled {
  at: number;
  fn: () => void;
}

export interface GameOptions {
  container: HTMLElement;
  characterId: string;
  onExit: (reason: string) => void;
}

const HOTBAR_ITEMS = ['minor_healing_pill', 'minor_qi_pill'];
const MAX_TICKS_PER_FRAME = 8;

/**
 * The client game: owns rendering, input, networking, prediction/reconciliation of the local
 * player, interpolation of everyone else, effects and the HUD bridge. The server stays
 * authoritative; everything here is presentation or prediction that the server corrects.
 */
export class Game {
  readonly renderer: GameRenderer;
  readonly scene = new THREE.Scene();
  readonly cam: CameraController;
  readonly input = new Input();
  readonly vfx = new Vfx();
  readonly overlay: Overlay;
  private readonly net: NetClient;
  private readonly canvas: HTMLCanvasElement;
  private world: WorldVisuals | null = null;
  zone: ZoneDef | null = null;
  layout: ZoneLayout | null = null;
  map: MapRenderer | null = null;
  readonly entities = new Map<number, EntityView>();
  private selfId = 0;
  private selfInfo: CharacterSelfDTO | null = null;
  private self: EntityView | null = null;

  // ---- prediction
  private pred: MoveState | null = null;
  private prevX = 0;
  private prevZ = 0;
  private pending: Pending[] = [];
  private seq = 0;
  private acc = 0;
  private errX = 0;
  private errZ = 0;
  private speedMul = 1;
  private canAct = true;
  private dead = false;
  private deadAt = 0;
  private queuedCast: { skillId: string; slot: number } | null = null;
  private dodgeQueued = false;
  private predictedCasts = new Set<number>();

  // ---- authoritative mirrors for HUD / prediction gating
  private hp = 0;
  private maxHp = 1;
  private mp = 0;
  private maxMp = 1;
  private statuses: SelfSnapshot['st'] = [];
  private cooldowns = new Map<string, { end: number; total: number }>();
  private targetId: number | null = null;
  private clockOffset = 0;
  private hasClock = false;

  // ---- frame loop
  private raf = 0;
  private last = 0;
  private time = 0;
  private scheduled: Scheduled[] = [];
  private hudAccum = 0;
  private mapAccum = 0;
  private fpsFrames = 0;
  private fpsTime = 0;
  private fpsHistory: number[] = [];
  private qualityAuto = true;
  minimapCanvas: HTMLCanvasElement | null = null;
  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();
  private readonly groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private aimPoint: THREE.Vector3 | null = null;
  private lastErrorAt = 0;
  private interactKey = '';
  private readonly selectionRing: THREE.Mesh;
  private stepDistance = 0;
  /** Auto-path (navigation assistance / test automation): waypoints still to walk. */
  private autopath: { x: number; z: number }[] = [];
  private nav: NavGrid | null = null;
  private disposed = false;
  private resizeObserver: ResizeObserver;
  private readonly unsubSettings: () => void;

  constructor(private readonly opts: GameOptions) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'game-canvas';
    this.canvas.tabIndex = 0;
    opts.container.prepend(this.canvas);
    this.renderer = new GameRenderer(this.canvas);
    this.cam = new CameraController(1);
    this.cam.setDistance(useSettings.getState().cameraDistance);
    this.overlay = new Overlay(opts.container);
    this.scene.add(this.vfx.group);
    this.scene.fog = new THREE.Fog('#c9d6d8', 70, 260);
    this.scene.background = new THREE.Color('#c9d6d8');

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.75, 0.92, 48),
      new THREE.MeshBasicMaterial({
        color: '#ff5a4a',
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.visible = false;
    this.selectionRing = ring;
    this.scene.add(ring);

    const s = useSettings.getState();
    this.qualityAuto = s.quality === 'auto';
    const q: QualityLevel = s.quality === 'auto' ? detectQuality(this.renderer.renderer) : s.quality;
    this.renderer.quality = q;
    this.overlay.enabledNumbers = s.showDamageNumbers;
    this.unsubSettings = useSettings.subscribe((st, prev) => {
      this.overlay.enabledNumbers = st.showDamageNumbers;
      if (st.quality !== prev.quality) {
        this.qualityAuto = st.quality === 'auto';
        this.applyQuality(st.quality === 'auto' ? detectQuality(this.renderer.renderer) : st.quality);
      }
      if (st.cameraDistance !== prev.cameraDistance) this.cam.setDistance(st.cameraDistance);
    });

    this.input.attach(this.canvas);
    this.input.onClick = (x, y) => this.onCanvasClick(x, y);
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(opts.container);
    this.resize();

    this.net = new NetClient(
      opts.characterId,
      (m) => this.onMessage(m),
      (status, detail) => {
        useGame.setState({ connection: status });
        if (status === 'offline') {
          useGame.setState({ kicked: detail ?? 'connection_lost' });
        }
      },
    );
    gameUi.reset();
    this.net.connect();
    this.last = performance.now();
    this.raf = requestAnimationFrame((t) => this.frame(t));
    if (__DEV__ || new URLSearchParams(location.search).has('debug')) {
      (window as unknown as { __jianghu?: unknown }).__jianghu = this.debugApi();
    }
  }

  // ===================================================================== public API (HUD)

  send(msg: object): void {
    this.net.send(msg);
  }
  sendChat(text: string): void {
    const t = text.trim();
    if (t) this.net.send({ t: 'chat', text: t.slice(0, 200) });
  }
  invOp(msg: Record<string, unknown>): void {
    this.net.send({ t: 'inv', ...msg });
  }
  npcOp(npc: number, op: 'talk' | 'accept' | 'turnin' | 'shop', quest?: string): void {
    this.net.send({ t: 'npc', npc, op, ...(quest ? { q: quest } : {}) });
  }
  shopOp(npc: number, op: 'buy' | 'sell', item: string, qty: number): void {
    this.net.send({ t: 'shop', npc, op, item, qty });
  }
  teleport(to: string): void {
    this.net.send({ t: 'tp', to });
    gameUi.toggle('waypoints', false);
  }
  respawn(): void {
    this.net.send({ t: 'respawn' });
  }
  abandonQuest(q: string): void {
    this.net.send({ t: 'quest', op: 'abandon', q });
  }
  triggerAction(a: Action): void {
    this.input.trigger(a);
  }
  setTarget(id: number | null): void {
    this.targetId = id;
    this.overlay.setTargeted(id);
    this.syncTarget();
  }

  leave(): void {
    this.opts.onExit('left');
  }

  // ===================================================================== networking

  private onMessage(m: ServerMessage): void {
    switch (m.t) {
      case 'welcome':
        this.onWelcome(m);
        break;
      case 's':
        this.onSnapshot(m.st, m.ack, m.me, m.e);
        break;
      case 'spawn':
        for (const d of m.e) this.spawn(d);
        break;
      case 'despawn':
        for (const id of m.ids) this.despawn(id);
        break;
      case 'cast':
        this.onCast(m.src, m.sk, m.step, m.ax, m.az, m.castMs, m.seq, m.tg);
        break;
      case 'castRej':
        this.onCastRejected(m.seq, m.sk, m.reason);
        break;
      case 'proj':
        if (m.src !== this.selfId) {
          const from = this.entities.get(m.src);
          this.vfx.crescent(
            new THREE.Vector3(m.x, from?.pos.y ?? 0, m.z),
            m.dx,
            m.dz,
            m.speed,
            m.dist,
            m.width,
            '#f2c96b',
          );
        }
        break;
      case 'hit':
        this.onHit(m.src, m.tgt, m.amt, m.kind, m.sk);
        break;
      case 'telegraph':
        this.vfx.telegraph(
          new THREE.Vector3(m.x, 0, m.z),
          m.dx,
          m.dz,
          m.shape,
          m.radius,
          m.halfAngleDeg,
          m.ms,
        );
        break;
      case 'death':
        this.onDeath(m.id);
        break;
      case 'revive': {
        const v = this.entities.get(m.id);
        v?.setDead(false, false);
        if (m.id === this.selfId) {
          this.dead = false;
          this.pushHud();
        }
        break;
      }
      case 'xp':
        this.onXp(m.gained, m.xp, m.level, m.xpNext);
        break;
      case 'level': {
        const v = this.entities.get(m.id);
        if (v) {
          this.vfx.levelUp(() => (this.entities.has(m.id) ? v.pos.clone() : null));
          if (m.id === this.selfId) audio.play('level_up');
        }
        break;
      }
      case 'stats':
        useGame.setState({ stats: m.stats });
        this.maxHp = m.stats.maxHp;
        this.maxMp = m.stats.maxMp;
        break;
      case 'skills':
        useGame.setState({ skills: m.skills });
        break;
      case 'inv':
        this.onInventory(m.items, m.coins);
        break;
      case 'coins':
        break;
      case 'quests':
        this.onQuests(m.quests);
        break;
      case 'markers':
        for (const [id, mark] of m.m) {
          this.overlay.setMarker(id, mark);
          const v = this.entities.get(id);
          if (v) v.dto = { ...v.dto, marker: mark };
        }
        break;
      case 'dialog':
        useGame.setState({
          dialog: {
            npc: m.npc,
            npcId: m.npcId,
            name: m.name,
            title: m.title,
            lines: m.lines,
            options: m.options,
          },
          shop: null,
        });
        break;
      case 'shop':
        useGame.setState({ shop: m.shop, dialog: null });
        gameUi.toggle('inventory', true);
        break;
      case 'toast':
        gameUi.toast(m.kind, m.text);
        if (m.kind === 'error') audio.play('ui_error');
        if (m.kind === 'quest')
          audio.play(m.text.startsWith('Quest complete') ? 'quest_complete' : 'quest_accept');
        if (m.kind === 'loot') audio.play(m.text.includes('coins') ? 'coins' : 'pickup');
        if (m.kind === 'loot' || m.kind === 'quest' || m.kind === 'level')
          gameUi.chat({ ch: 'system', text: m.text, at: Date.now() });
        break;
      case 'chat':
        gameUi.chat({ ch: m.ch, ...(m.from ? { from: m.from } : {}), text: m.text, at: m.at });
        if (m.fromId && m.ch === 'zone') {
          const v = this.entities.get(m.fromId) ?? (m.fromId === this.selfId ? this.self : undefined);
          if (v)
            this.overlay.floatText(
              v.headPos(new THREE.Vector3()).add(new THREE.Vector3(0, 0.4, 0)),
              m.text.slice(0, 60),
              'speech',
              4,
            );
        }
        break;
      case 'pong': {
        useGame.setState({ ping: Math.round(this.net.rtt) });
        break;
      }
      case 'tele':
        this.onTeleport(m.x, m.z);
        break;
      case 'cd': {
        const now = performance.now();
        for (const [k, ms] of m.cds) this.cooldowns.set(k, { end: now + ms, total: ms });
        useGame.setState({ cooldowns: Object.fromEntries(this.cooldowns) });
        break;
      }
      case 'area':
        useGame.setState({ area: { name: m.name, at: Date.now() } });
        break;
      case 'kick':
        useGame.setState({ kicked: m.reason });
        break;
    }
  }

  private onWelcome(m: Extract<ServerMessage, { t: 'welcome' }>): void {
    if (!this.world || this.zone?.id !== m.zoneId) this.buildZone(m.zoneId);
    for (const id of [...this.entities.keys()]) this.removeView(id);
    this.selfId = m.self.entityId;
    this.selfInfo = m.self;
    this.pred = createMoveState(m.move.x, m.move.z, m.move.fx, m.move.fz);
    this.applySelfSnapshot(m.move);
    this.prevX = this.pred.x;
    this.prevZ = this.pred.z;
    this.errX = 0;
    this.errZ = 0;
    this.pending = [];
    this.seq = Math.max(this.seq, m.lastSeq);
    this.hasClock = false;
    const now = performance.now();
    this.cooldowns.clear();
    for (const [k, ms] of m.cooldowns) this.cooldowns.set(k, { end: now + ms, total: ms });
    this.hp = m.move.hp;
    this.mp = m.move.mp;
    this.maxHp = m.stats.maxHp;
    this.maxMp = m.stats.maxMp;
    this.dead = m.move.dead;
    const equipped = (slot: string): string | null =>
      m.inventory.find((i) => i.equipSlot === slot)?.defId ?? null;
    const selfDto: EntitySpawnDTO = {
      id: m.self.entityId,
      k: 'player',
      name: m.self.name,
      level: m.self.level,
      x: m.move.x,
      z: m.move.z,
      yaw: Math.atan2(m.move.fx, m.move.fz),
      hp: m.move.hp,
      maxHp: m.move.mhp,
      player: {
        sect: m.self.sect,
        appearance: m.self.appearance,
        weapon: equipped('weapon'),
        armor: equipped('chest'),
      },
    };
    const view = new EntityView(selfDto);
    view.isLocal = true;
    this.self = view;
    this.entities.set(view.id, view);
    this.scene.add(view.root);
    if (this.dead) view.setDead(true, true);
    this.cam.snapTo(m.move.x, 0, m.move.z);
    useGame.setState({
      loading: false,
      zoneName: this.zone?.name ?? '',
      stats: m.stats,
      inventory: m.inventory,
      quests: m.quests,
      skills: m.skills,
      cooldowns: Object.fromEntries(this.cooldowns),
      kicked: null,
      self: {
        entityId: m.self.entityId,
        name: m.self.name,
        sect: m.self.sect,
        level: m.self.level,
        xp: m.self.xp,
        xpNext: m.self.xpNext,
        coins: m.self.coins,
        hp: m.move.hp,
        maxHp: m.move.mhp,
        mp: m.move.mp,
        maxMp: m.move.mmp,
        stamina: m.move.sta,
        dead: m.move.dead,
        deadAt: 0,
        statuses: m.move.st,
        isAdmin: m.self.isAdmin,
      },
    });
    audio.startMusic();
    audio.startAmbience();
  }

  private buildZone(zoneId: string): void {
    const zone = getZone(zoneId) ?? getZone(STARTING_ZONE_ID);
    if (!zone) throw new Error('zone missing');
    if (this.world) {
      this.scene.remove(this.world.group);
      this.world.dispose();
    }
    this.zone = zone;
    this.layout = getZoneLayout(zone);
    const p = PRESETS[this.renderer.quality];
    this.world = buildWorld(zone, this.layout, {
      textureSize: this.renderer.quality === 'low' ? 1024 : this.renderer.quality === 'medium' ? 2048 : 4096,
      grassDensity: p.grassDensity,
      shadowSize: p.shadowSize,
    });
    this.scene.add(this.world.group);
    this.scene.fog = new THREE.Fog(zone.ambience.fogColor, 75, 270);
    this.scene.background = new THREE.Color(zone.ambience.fogColor);
    this.map = new MapRenderer(zone, this.layout);
    const layout = this.layout;
    this.vfx.blocked = (x, z) => layout.world.overlaps(x, z, 0.15);
    this.applyQuality(this.renderer.quality);
  }

  private applyQuality(q: QualityLevel): void {
    this.renderer.setQuality(q, this.scene, this.cam.camera);
    this.vfx.particles.budgetScale = q === 'low' ? 0.45 : q === 'medium' ? 0.75 : 1;
    if (this.world) this.world.sun.castShadow = PRESETS[q].shadows;
  }

  private spawn(d: EntitySpawnDTO): void {
    if (d.id === this.selfId) return;
    const existing = this.entities.get(d.id);
    if (existing && existing.fading === null) {
      existing.refresh(d);
      if (d.marker !== undefined) this.overlay.setMarker(d.id, d.marker);
      return;
    }
    if (existing) this.removeView(d.id);
    const v = new EntityView(d);
    this.entities.set(d.id, v);
    this.scene.add(v.root);
    if (d.k !== 'loot') {
      this.overlay.addPlate(d.id, {
        name: d.name,
        ...(d.k === 'npc' && d.title ? { title: d.title } : {}),
        ...(d.level !== undefined && d.k !== 'npc' ? { level: d.level } : {}),
        kind: d.k,
        showHp: d.k === 'monster' || d.k === 'player',
      });
      if (d.hp !== undefined && d.maxHp) this.overlay.setHp(d.id, d.hp / d.maxHp);
      if (d.marker) this.overlay.setMarker(d.id, d.marker);
      if (d.state === ENTITY_STATE.dead) this.overlay.setDim(d.id, true);
    }
  }

  private despawn(id: number): void {
    const v = this.entities.get(id);
    if (!v || v.isLocal) return;
    this.overlay.removePlate(id);
    if (v.dto.k === 'monster' || v.dto.k === 'loot') v.fading = 0.6;
    else this.removeView(id);
    if (this.targetId === id) this.setTarget(null);
  }

  private removeView(id: number): void {
    const v = this.entities.get(id);
    if (!v) return;
    this.scene.remove(v.root);
    v.dispose();
    this.entities.delete(id);
    this.overlay.removePlate(id);
  }

  private applySelfSnapshot(me: SelfSnapshot): void {
    const p = this.pred as MoveState;
    p.x = me.x;
    p.z = me.z;
    p.fx = me.fx;
    p.fz = me.fz;
    p.stamina = me.sta;
    p.dashT = me.dashT;
    p.dashX = me.dashX;
    p.dashZ = me.dashZ;
    p.dashCd = me.dashCd;
    p.rootTicks = me.rt;
    p.sprintLocked = me.sl;
    p.comboStep = me.cs;
    p.comboTicks = me.ct;
    this.speedMul = me.sm;
    this.canAct = me.ca;
  }

  private onSnapshot(
    st: number,
    ack: number,
    me: SelfSnapshot,
    tuples: [number, number, number, number, number, number, number][],
  ): void {
    const now = performance.now();
    const sample = st - now;
    if (!this.hasClock) {
      this.clockOffset = sample;
      this.hasClock = true;
    } else if (Math.abs(sample - this.clockOffset) > 400) {
      this.clockOffset = sample;
    } else {
      this.clockOffset += (sample - this.clockOffset) * 0.05;
    }
    if (this.pred && this.layout) {
      const beforeX = this.pred.x;
      const beforeZ = this.pred.z;
      this.pending = this.pending.filter((p) => p.seq > ack);
      for (const s of [...this.predictedCasts]) if (s <= ack) this.predictedCasts.delete(s);
      this.applySelfSnapshot(me);
      for (const p of this.pending) this.simulate(p);
      const ex = beforeX - this.pred.x;
      const ez = beforeZ - this.pred.z;
      if (Math.hypot(ex + this.errX, ez + this.errZ) > 4) {
        this.errX = 0;
        this.errZ = 0;
      } else {
        this.errX += ex;
        this.errZ += ez;
      }
    }
    this.hp = me.hp;
    this.maxHp = me.mhp;
    this.mp = me.mp;
    this.maxMp = me.mmp;
    this.statuses = me.st;
    if (me.dead !== this.dead) {
      this.dead = me.dead;
      if (this.dead) this.deadAt = Date.now();
      this.self?.setDead(this.dead, false);
    }
    if (this.self && this.self.anim) this.self.anim.stunned = !me.ca && !me.dead;
    for (const tup of tuples) {
      const v = this.entities.get(tup[0]);
      if (!v || v.isLocal) continue;
      v.pushSnapshot(st, tup);
      if (v.maxHp > 0 && (v.dto.k === 'monster' || v.dto.k === 'player'))
        this.overlay.setHp(v.id, v.hp / v.maxHp);
    }
  }

  private onCast(
    src: number,
    sk: string,
    step: number,
    ax: number,
    az: number,
    castMs: number,
    seq?: number,
    tg?: number,
  ): void {
    if (src === this.selfId && seq !== undefined && this.predictedCasts.has(seq)) return;
    const v = this.entities.get(src);
    if (!v) return;
    if (tg !== undefined && src !== this.selfId) {
      const t = this.entities.get(tg);
      if (t) v.yaw = Math.atan2(t.pos.x - v.pos.x, t.pos.z - v.pos.z);
    } else if (src !== this.selfId) v.yaw = Math.atan2(ax, az);
    playCastFx(v, sk, step, ax, az, castMs, this.fxContext(src === this.selfId));
    if (src === this.selfId && sk === 'wudang_sword_qi_crescent') this.predictProjectile(sk, castMs, ax, az);
  }

  private onCastRejected(seq: number, sk: string, reason: string): void {
    const p = this.pending.find((x) => x.seq === seq);
    if (p) delete p.action;
    if (this.predictedCasts.delete(seq)) {
      this.self?.anim?.cancelAction();
      const skill = getSkill(sk);
      if (skill?.cooldownMs) this.cooldowns.delete(sk);
      useGame.setState({ cooldowns: Object.fromEntries(this.cooldowns), castBar: null });
    }
    this.showError(CAST_REJECT_TEXT[reason as CastRejectReason] ?? 'Technique failed.');
  }

  private onHit(src: number, tgt: number, amt: number, kind: string, sk: string): void {
    const t = this.entities.get(tgt);
    if (!t) return;
    const at = t.chestPos(new THREE.Vector3());
    const mine = src === this.selfId;
    const onMe = tgt === this.selfId;
    if (kind === 'heal') {
      this.vfx.heal(t.pos, sk === 'item_qi' ? '#7fc8ff' : '#7fff9a');
      this.overlay.floatText(at, `+${amt}`, sk === 'item_qi' ? 'heal qi' : 'heal');
      if (onMe) audio.play('heal');
      return;
    }
    if (kind === 'evade' || kind === 'dodge') {
      this.overlay.floatText(at, kind === 'dodge' ? 'Dodged!' : 'Miss', onMe ? 'miss me' : 'miss');
      if (kind === 'dodge' && onMe) audio.play('dodge');
      return;
    }
    const crit = kind === 'crit';
    t.flinch();
    const element = sk.startsWith('wudang') ? '#ffd77a' : '#ff6a4a';
    this.vfx.impact(at, element, crit);
    this.overlay.floatText(
      at,
      crit ? `${amt}!` : String(amt),
      onMe ? 'dmg-in' : mine ? (crit ? 'dmg-out crit' : 'dmg-out') : 'dmg-other',
    );
    if (mine || onMe) audio.play(crit ? 'crit' : onMe ? 'player_hurt' : 'hit');
    if (onMe) this.cam.shake(crit ? 0.35 : 0.18);
    else if (mine && crit) this.cam.shake(0.12);
    if (t.dto.k !== 'player') this.overlay.setHp(tgt, Math.max(0, t.hp - amt) / Math.max(1, t.maxHp));
  }

  private onDeath(id: number): void {
    const v = this.entities.get(id);
    if (!v) return;
    v.setDead(true, false);
    this.overlay.setDim(id, true);
    if (id === this.selfId) {
      this.dead = true;
      this.deadAt = Date.now();
      audio.play('death');
      this.pushHud();
    } else if (v.dto.k === 'monster') {
      audio.play('monster_die', { volume: 0.7 });
    }
  }

  private onXp(gained: number, xp: number, level: number, xpNext: number): void {
    const s = useGame.getState().self;
    if (s) useGame.setState({ self: { ...s, xp, level, xpNext } });
    if (gained > 0 && this.self) {
      this.overlay.floatText(this.self.headPos(new THREE.Vector3()), `+${gained} XP`, 'xp', 1.4);
    }
    if (this.selfInfo) this.selfInfo = { ...this.selfInfo, level, xp, xpNext };
  }

  private onInventory(items: Extract<ServerMessage, { t: 'inv' }>['items'], coins: number): void {
    const prev = useGame.getState().inventory;
    const s = useGame.getState().self;
    useGame.setState({ inventory: items, ...(s ? { self: { ...s, coins } } : {}) });
    const before = (slot: string): string | null => prev.find((i) => i.equipSlot === slot)?.defId ?? null;
    const after = (slot: string): string | null => items.find((i) => i.equipSlot === slot)?.defId ?? null;
    if (this.self && (before('weapon') !== after('weapon') || before('chest') !== after('chest'))) {
      audio.play('equip');
      const dto = this.self.dto;
      if (dto.player)
        this.self.refresh({
          ...dto,
          player: { ...dto.player, weapon: after('weapon'), armor: after('chest') },
        });
    }
  }

  private onQuests(quests: Extract<ServerMessage, { t: 'quests' }>['quests']): void {
    const prev = useGame.getState().quests;
    for (const q of quests) {
      const p = prev.find((x) => x.id === q.id);
      if (q.ready && p && !p.ready && q.status === 'active')
        gameUi.toast('quest', 'Quest objectives complete — return to turn in.', `ready:${q.id}`);
    }
    // Withdraw the reminder as soon as the quest is turned in or abandoned.
    for (const p of prev) {
      const q = quests.find((x) => x.id === p.id);
      if (p.ready && (!q || q.status !== 'active')) gameUi.dismissToast(`ready:${p.id}`);
    }
    useGame.setState({ quests });
  }

  private onTeleport(x: number, z: number): void {
    if (!this.pred) return;
    this.vfx.teleport(new THREE.Vector3(this.pred.x, 0, this.pred.z));
    this.pred.x = x;
    this.pred.z = z;
    this.prevX = x;
    this.prevZ = z;
    this.errX = 0;
    this.errZ = 0;
    this.cam.snapTo(x, 0, z);
    this.vfx.teleport(new THREE.Vector3(x, 0, z));
    audio.play('teleport');
  }

  // ===================================================================== prediction

  private moveCtx() {
    return {
      world: (this.layout as ZoneLayout).world,
      radius: HUMANOID_RADIUS,
      speedMul: this.speedMul,
      canAct: this.canAct && !this.dead,
    };
  }

  private simulate(p: Pending): void {
    const pred = this.pred as MoveState;
    if (p.action) {
      const skill = getSkill(p.action.sk);
      if (skill) applyCastToMoveState(pred, skill, p.action.ax, p.action.az);
    }
    stepMovement(pred, p.cmd, this.moveCtx(), INPUT_DT);
  }

  /** One fixed 30 Hz input tick: sample input, predict, send. */
  private inputTick(): void {
    if (!this.pred || !this.layout) return;
    const axis = this.input.moveAxis();
    let mx = 0;
    let mz = 0;
    if (axis.x !== 0 || axis.y !== 0) {
      const f = this.cam.forward();
      const r = this.cam.right();
      mx = f.x * axis.y + r.x * axis.x;
      mz = f.z * axis.y + r.z * axis.x;
      const l = Math.hypot(mx, mz);
      if (l > 1) {
        mx /= l;
        mz /= l;
      }
      this.autopath = [];
    } else if (this.autopath.length > 0) {
      const wp = this.autopath[0] as { x: number; z: number };
      const dx = wp.x - this.pred.x;
      const dz = wp.z - this.pred.z;
      const d = Math.hypot(dx, dz);
      if (d < (this.autopath.length === 1 ? 0.5 : 0.8)) this.autopath.shift();
      if (d > 0.05) {
        mx = dx / d;
        mz = dz / d;
      }
    }
    const cmd: MoveCommand = { mx, mz, sprint: this.input.sprint(), dodge: this.dodgeQueued };
    this.dodgeQueued = false;
    const seq = ++this.seq;
    const p: Pending = { seq, cmd };

    // Held basic attack (mouse or key 1) repeats as soon as the previous swing allows.
    if (
      !this.queuedCast &&
      (this.input.isHeld('skill0') || (this.input.primaryHeld && this.input.mouseInside))
    ) {
      const basic = this.hotbarSkill(0);
      if (basic && this.pred.rootTicks === 0) this.queuedCast = { skillId: basic, slot: 0 };
    }
    if (this.queuedCast && this.pred.rootTicks === 0 && this.pred.dashT <= 0) {
      const action = this.buildCast(this.queuedCast.skillId);
      this.queuedCast = null;
      if (action) {
        p.action = action;
        this.startPredictedCast(action, seq);
      }
    }
    if (
      cmd.dodge &&
      this.pred.dashT <= 0 &&
      this.pred.dashCd <= 0 &&
      this.pred.stamina >= 25 &&
      this.canAct &&
      !this.dead &&
      this.pred.rootTicks === 0
    ) {
      this.self?.playAction('DODGE', 0.3);
      audio.play('dodge', { volume: 0.7 });
      this.vfx.dust(new THREE.Vector3(this.pred.x, 0, this.pred.z), 8);
    }
    this.prevX = this.pred.x;
    this.prevZ = this.pred.z;
    this.simulate(p);
    this.pending.push(p);
    if (this.pending.length > 90) this.pending.shift();
    this.net.send({
      t: 'in',
      s: seq,
      mx: round3(mx),
      mz: round3(mz),
      sp: cmd.sprint,
      dg: cmd.dodge,
      ...(p.action ? { a: p.action } : {}),
    });

    // Footsteps follow the distance travelled.
    if (this.pred.moving) {
      this.stepDistance += Math.hypot(this.pred.x - this.prevX, this.pred.z - this.prevZ);
      if (this.stepDistance > (this.pred.sprinting ? 1.8 : 1.25)) {
        this.stepDistance = 0;
        audio.play('footstep', { volume: 0.6, pitch: 0.9 + Math.random() * 0.2 });
      }
    }
  }

  private hotbarSkill(slot: number): string | null {
    const sect = this.selfInfo ? getSect(this.selfInfo.sect) : undefined;
    const id = sect?.skills[slot];
    if (!id) return null;
    return useGame.getState().skills.includes(id) ? id : null;
  }

  private equippedWeaponType(): string | null {
    const w = useGame.getState().inventory.find((i) => i.equipSlot === 'weapon');
    return (w && getItem(w.defId)?.weaponType) ?? null;
  }

  /** Builds and locally validates a technique activation; returns null (with feedback) if invalid. */
  private buildCast(skillId: string): CastAction | null {
    const skill = getSkill(skillId);
    const pred = this.pred;
    if (!skill || !pred || !this.selfInfo) return null;
    let target = this.targetId !== null ? this.entities.get(this.targetId) : undefined;
    if (target && (target.dto.k !== 'monster' || target.dead || target.fading !== null)) target = undefined;
    if (
      skill.requiresTarget &&
      (!target || dist(pred.x, pred.z, target.pos.x, target.pos.z) > skill.range + 3)
    ) {
      const auto = this.nearestHostile(skill.range + 2.5);
      if (auto) {
        target = auto;
        this.setTarget(auto.id);
      }
    }
    let aimX = pred.fx;
    let aimZ = pred.fz;
    if (target && dist(pred.x, pred.z, target.pos.x, target.pos.z) < Math.max(8, skill.range + 3)) {
      const d = normalize(target.pos.x - pred.x, target.pos.z - pred.z);
      aimX = d.x;
      aimZ = d.z;
    } else if (this.aimPoint && this.input.mouseInside) {
      const d = normalize(this.aimPoint.x - pred.x, this.aimPoint.z - pred.z);
      if (d.x !== 0 || d.z !== 0) {
        aimX = d.x;
        aimZ = d.z;
      }
    }
    const cd = this.cooldowns.get(skill.id);
    const v = validateCast({
      skill,
      casterSect: this.selfInfo.sect,
      casterLevel: useGame.getState().self?.level ?? 1,
      alive: !this.dead,
      stunned: !this.canAct,
      rootTicks: pred.rootTicks,
      dashing: pred.dashT > 0,
      weapon: this.equippedWeaponType() as never,
      mp: this.mp,
      stamina: pred.stamina,
      cooldownReady: !cd || cd.end <= performance.now(),
      target: target
        ? {
            alive: !target.dead,
            hostile: target.dto.k === 'monster',
            distance: dist(pred.x, pred.z, target.pos.x, target.pos.z),
            radius: 0.5,
          }
        : undefined,
    });
    if (!v.ok) {
      this.showError(CAST_REJECT_TEXT[v.reason]);
      return null;
    }
    return { sk: skill.id, ...(target ? { tg: target.id } : {}), ax: round3(aimX), az: round3(aimZ) };
  }

  private startPredictedCast(a: CastAction, seq: number): void {
    const skill = getSkill(a.sk);
    if (!skill || !this.self || !this.pred) return;
    const step =
      skill.combo?.length && this.pred.comboTicks > 0 ? this.pred.comboStep % skill.combo.length : 0;
    const hits = skillStepHits(skill, step);
    const castMs = hits[0]?.atMs ?? skill.castTimeMs;
    this.predictedCasts.add(seq);
    if (skill.cooldownMs > 0) {
      this.cooldowns.set(skill.id, { end: performance.now() + skill.cooldownMs, total: skill.cooldownMs });
      useGame.setState({ cooldowns: Object.fromEntries(this.cooldowns) });
    }
    this.self.yaw = Math.atan2(a.ax, a.az);
    playCastFx(this.self, skill.id, step, a.ax, a.az, castMs, this.fxContext(true));
    if (skill.id === 'wudang_sword_qi_crescent') this.predictProjectile(skill.id, castMs, a.ax, a.az);
    if (castMs >= 300) {
      const now = performance.now();
      useGame.setState({ castBar: { name: skill.name, start: now, end: now + castMs } });
    }
  }

  private predictProjectile(skillId: string, castMs: number, ax: number, az: number): void {
    const skill = getSkill(skillId);
    const pr = skill?.projectile;
    if (!pr) return;
    this.schedule(castMs / 1000, () => {
      if (!this.pred || this.dead) return;
      const ox = this.pred.x + ax * 0.6;
      const oz = this.pred.z + az * 0.6;
      this.vfx.crescent(new THREE.Vector3(ox, 0, oz), ax, az, pr.speed, pr.maxDistance, pr.width, '#f2c96b');
    });
  }

  private nearestHostile(range: number): EntityView | undefined {
    if (!this.pred) return undefined;
    let best: EntityView | undefined;
    let bestScore = Infinity;
    for (const v of this.entities.values()) {
      if (v.dto.k !== 'monster' || v.dead || v.fading !== null) continue;
      const d = dist(this.pred.x, this.pred.z, v.pos.x, v.pos.z);
      if (d > range) continue;
      const dir = normalize(v.pos.x - this.pred.x, v.pos.z - this.pred.z);
      const facing = dir.x * this.pred.fx + dir.z * this.pred.fz;
      const score = d - facing * 2;
      if (score < bestScore) {
        bestScore = score;
        best = v;
      }
    }
    return best;
  }

  // ===================================================================== input handling

  private handleActions(): void {
    for (const a of this.input.drain()) {
      switch (a) {
        case 'skill0':
        case 'skill1':
        case 'skill2':
        case 'skill3': {
          const slot = Number(a.slice(5));
          const id = this.hotbarSkill(slot);
          if (!id) {
            const sect = this.selfInfo ? getSect(this.selfInfo.sect) : undefined;
            const sid = sect?.skills[slot];
            const def = sid ? getSkill(sid) : undefined;
            this.showError(
              def ? `${def.name} unlocks at level ${def.levelRequired}.` : 'No technique in that slot.',
            );
            break;
          }
          this.queuedCast = { skillId: id, slot };
          break;
        }
        case 'item1':
        case 'item2':
          this.useHotbarItem(a === 'item1' ? 0 : 1);
          break;
        case 'dodge':
          this.dodgeQueued = true;
          break;
        case 'interact':
          this.interact();
          break;
        case 'target_next':
          this.cycleTarget();
          break;
        case 'escape':
          if (!gameUi.closeAll()) {
            if (this.targetId !== null) this.setTarget(null);
            else gameUi.toggle('menu', true);
          }
          break;
        case 'inventory':
          gameUi.toggle('inventory');
          audio.play('ui_click');
          break;
        case 'character':
          gameUi.toggle('character');
          audio.play('ui_click');
          break;
        case 'quests':
          gameUi.toggle('quests');
          audio.play('ui_click');
          break;
        case 'map':
          gameUi.toggle('map');
          audio.play('ui_click');
          break;
        case 'help':
          gameUi.toggle('help');
          break;
        case 'chat':
          window.dispatchEvent(new CustomEvent('jh-focus-chat'));
          break;
        case 'cam_left':
          this.cam.rotate(-Math.PI / 4);
          break;
        case 'cam_right':
          this.cam.rotate(Math.PI / 4);
          break;
      }
    }
  }

  useHotbarItem(index: number): void {
    const defId = HOTBAR_ITEMS[index];
    if (!defId) return;
    const inv = useGame.getState().inventory;
    const it = inv.find((i) => i.defId === defId && i.bagSlot !== null);
    if (!it) {
      this.showError(`No ${getItem(defId)?.name ?? 'items'} left.`);
      return;
    }
    this.invOp({ op: 'use', item: it.id });
  }

  hotbarItemCount(index: number): number {
    const defId = HOTBAR_ITEMS[index];
    return defId ? countInBag(useGame.getState().inventory as never, defId) : 0;
  }

  private interactables(): {
    kind: 'npc' | 'loot' | 'shrine';
    id: number | string;
    label: string;
    d: number;
  }[] {
    const out: { kind: 'npc' | 'loot' | 'shrine'; id: number | string; label: string; d: number }[] = [];
    if (!this.pred || !this.zone) return out;
    for (const v of this.entities.values()) {
      if (v.fading !== null) continue;
      const d = dist(this.pred.x, this.pred.z, v.pos.x, v.pos.z);
      if (v.dto.k === 'npc' && d <= INTERACT_RANGE + 1)
        out.push({ kind: 'npc', id: v.id, label: `Talk to ${v.dto.name}`, d });
      if (v.dto.k === 'loot' && d <= INTERACT_RANGE - 0.3)
        out.push({ kind: 'loot', id: v.id, label: 'Pick up loot', d: d - 1 });
    }
    for (const w of this.zone.waypoints) {
      const d = dist(this.pred.x, this.pred.z, w.x, w.z);
      if (d <= INTERACT_RANGE + 0.5) out.push({ kind: 'shrine', id: w.id, label: `Waypoint: ${w.name}`, d });
    }
    return out.sort((a, b) => a.d - b.d);
  }

  private interact(): void {
    const it = this.interactables()[0];
    if (!it) return;
    if (it.kind === 'npc') {
      this.npcOp(it.id as number, 'talk');
      audio.play('ui_click');
    } else if (it.kind === 'loot') this.net.send({ t: 'pickup', id: it.id });
    else gameUi.toggle('waypoints', true);
  }

  private cycleTarget(): void {
    if (!this.pred) return;
    const list = [...this.entities.values()]
      .filter(
        (v) =>
          v.dto.k === 'monster' &&
          !v.dead &&
          v.fading === null &&
          dist(this.pred!.x, this.pred!.z, v.pos.x, v.pos.z) < 28,
      )
      .sort(
        (a, b) =>
          dist(this.pred!.x, this.pred!.z, a.pos.x, a.pos.z) -
          dist(this.pred!.x, this.pred!.z, b.pos.x, b.pos.z),
      );
    if (!list.length) {
      this.setTarget(null);
      return;
    }
    const idx = list.findIndex((v) => v.id === this.targetId);
    this.setTarget((list[(idx + 1) % list.length] as EntityView).id);
  }

  private onCanvasClick(x: number, y: number): void {
    const hit = this.pick(x, y);
    if (!hit) return;
    if (hit.dto.k === 'npc') {
      this.setTarget(hit.id);
      if (this.pred && dist(this.pred.x, this.pred.z, hit.pos.x, hit.pos.z) <= INTERACT_RANGE + 1.5)
        this.npcOp(hit.id, 'talk');
      else this.showError('Move closer to talk.');
      this.input.primaryHeld = false;
    } else if (hit.dto.k === 'loot') {
      this.net.send({ t: 'pickup', id: hit.id });
      this.input.primaryHeld = false;
    } else if (hit.dto.k === 'monster' || hit.dto.k === 'player') {
      this.setTarget(hit.id);
      if (hit.dto.k === 'player') this.input.primaryHeld = false;
    }
  }

  private pick(x: number, y: number): EntityView | null {
    const r = this.canvas.getBoundingClientRect();
    this.ndc.set((x / r.width) * 2 - 1, -(y / r.height) * 2 + 1);
    this.raycaster.setFromCamera(this.ndc, this.cam.camera);
    const proxies = [...this.entities.values()]
      .filter((v) => !v.isLocal && v.fading === null)
      .map((v) => v.proxy);
    const hits = this.raycaster.intersectObjects(proxies, false);
    const first = hits[0];
    if (!first) return null;
    return this.entities.get(first.object.userData.entityId as number) ?? null;
  }

  // ===================================================================== frame loop

  private schedule(delaySec: number, fn: () => void): void {
    this.scheduled.push({ at: this.time + delaySec, fn });
  }

  private fxContext(isSelf: boolean): FxContext {
    return {
      vfx: this.vfx,
      isSelf,
      schedule: (d, fn) => this.schedule(d, fn),
      sfx: (name: SfxName, pos?: THREE.Vector3, volume = 1) => {
        let v = volume;
        if (pos && this.pred && !isSelf)
          v *= Math.max(0, 1 - dist(pos.x, pos.z, this.pred.x, this.pred.z) / 30);
        if (v > 0.03) audio.play(name, { volume: v });
      },
      shake: (amount, at) => {
        let a = amount;
        if (at && this.pred) a *= Math.max(0, 1 - dist(at.x, at.z, this.pred.x, this.pred.z) / 12);
        if (a > 0.01) this.cam.shake(a);
      },
    };
  }

  private frame(now: number): void {
    if (this.disposed) return;
    this.raf = requestAnimationFrame((t) => this.frame(t));
    const dt = Math.min(0.25, (now - this.last) / 1000);
    this.last = now;
    this.time += dt;

    this.cam.zoom(this.input.takeWheel());
    const drag = this.input.takeDragYaw();
    if (drag) this.cam.rotate(-drag * 0.008);
    this.handleActions();

    if (this.pred && this.layout) {
      this.acc += dt;
      let ticks = 0;
      while (this.acc >= INPUT_DT && ticks < MAX_TICKS_PER_FRAME) {
        this.inputTick();
        this.acc -= INPUT_DT;
        ticks++;
      }
      if (ticks === MAX_TICKS_PER_FRAME) this.acc = 0;
    }

    for (let i = this.scheduled.length - 1; i >= 0; i--) {
      const s = this.scheduled[i] as Scheduled;
      if (s.at <= this.time) {
        this.scheduled.splice(i, 1);
        s.fn();
      }
    }

    const groundHeight = this.world ? this.world.groundHeight.bind(this.world) : () => 0;
    if (this.pred && this.self) {
      const alpha = Math.min(1, this.acc / INPUT_DT);
      const decay = Math.exp(-10 * dt);
      this.errX *= decay;
      this.errZ *= decay;
      const rx = this.prevX + (this.pred.x - this.prevX) * alpha + this.errX;
      const rz = this.prevZ + (this.pred.z - this.prevZ) * alpha + this.errZ;
      const yaw = Math.atan2(this.pred.fx, this.pred.fz);
      this.self.setPredicted(rx, rz, yaw);
      if (this.self.anim) this.self.anim.stunned = !this.canAct && !this.dead;
    }
    const renderTime = performance.now() + this.clockOffset - INTERPOLATION_DELAY_MS;
    for (const [id, v] of this.entities) {
      v.update(dt, renderTime, groundHeight);
      if (v.fading !== null && v.fading <= 0) this.removeView(id);
    }

    const focus = this.self ? this.self.pos : new THREE.Vector3();
    this.cam.update(dt, focus.x, focus.y, focus.z);
    this.world?.update(this.time, dt, focus);
    this.vfx.update(dt);
    this.updateSelectionRing();
    this.updateAim();

    const r = this.canvas.getBoundingClientRect();
    this.overlay.update(dt, this.cam.camera, r.width, r.height, (id) => {
      const v = this.entities.get(id);
      return v && v.fading === null ? v.headPos(tmpV) : null;
    });

    this.hudAccum += dt;
    if (this.hudAccum >= 0.1) {
      this.hudAccum = 0;
      this.pushHud();
      this.updateInteractHint();
    }
    this.mapAccum += dt;
    if (this.mapAccum >= 0.08 && this.minimapCanvas && this.map && this.pred) {
      this.mapAccum = 0;
      this.map.drawMinimap(
        this.minimapCanvas,
        this.pred.x,
        this.pred.z,
        Math.atan2(this.pred.fx, this.pred.fz),
        this.cam.yaw,
        this.markers(),
      );
    }

    this.renderer.render(this.scene, this.cam.camera);
    this.trackFps(dt);
  }

  private updateAim(): void {
    if (!this.input.mouseInside) return;
    const r = this.canvas.getBoundingClientRect();
    this.ndc.set((this.input.mouseX / r.width) * 2 - 1, -(this.input.mouseY / r.height) * 2 + 1);
    this.raycaster.setFromCamera(this.ndc, this.cam.camera);
    this.aimPoint = this.raycaster.ray.intersectPlane(this.groundPlane, this.aimPoint ?? new THREE.Vector3());
  }

  private updateSelectionRing(): void {
    const t = this.targetId !== null ? this.entities.get(this.targetId) : undefined;
    if (!t || t.fading !== null) {
      this.selectionRing.visible = false;
      return;
    }
    this.selectionRing.visible = true;
    this.selectionRing.position.set(t.pos.x, t.pos.y + 0.05, t.pos.z);
    const s = t.dto.k === 'monster' && t.wolf ? 1.15 : 0.9;
    this.selectionRing.scale.setScalar(s * (1 + Math.sin(this.time * 5) * 0.04));
    (this.selectionRing.material as THREE.MeshBasicMaterial).color.set(
      t.dto.k === 'monster' ? '#ff5a4a' : t.dto.k === 'npc' ? '#ecd08e' : '#8cc1f2',
    );
  }

  markers(): MapMarker[] {
    const out: MapMarker[] = [];
    for (const v of this.entities.values()) {
      if (v.isLocal || v.fading !== null) continue;
      if (v.dto.k === 'npc') {
        const mark = v.dto.marker;
        out.push({ x: v.pos.x, z: v.pos.z, kind: mark === '!' ? 'quest' : mark === '?' ? 'turnin' : 'npc' });
      } else if (v.dto.k === 'monster')
        out.push({ x: v.pos.x, z: v.pos.z, kind: v.dead ? 'dead' : 'monster' });
      else if (v.dto.k === 'player') out.push({ x: v.pos.x, z: v.pos.z, kind: 'player' });
      else out.push({ x: v.pos.x, z: v.pos.z, kind: 'loot' });
    }
    for (const w of this.zone?.waypoints ?? []) out.push({ x: w.x, z: w.z, kind: 'waypoint' });
    return out;
  }

  /**
   * Navigation assistance: plans an A* path on the same navigation grid the server uses and walks it.
   * Any manual movement input cancels it. Returns false when no path exists.
   */
  travelTo(x: number, z: number): boolean {
    if (!this.pred || !this.layout) return false;
    this.nav ??= new NavGrid(this.layout.world, 0.5, 1);
    const path = this.nav.findPath(this.pred.x, this.pred.z, x, z, 30000);
    if (!path) {
      this.showError('No path to that location.');
      return false;
    }
    this.autopath = path;
    return true;
  }

  /** Auto-travel to the next destination of a quest (turn-in NPC or first unfinished objective). */
  travelToQuest(questId: string): void {
    const zone = this.zone;
    const def = getQuest(questId);
    const prog = useGame.getState().quests.find((q) => q.id === questId);
    if (!zone || !def || !prog) return;
    const npcPos = (npcId: string): { x: number; z: number } | null => {
      const p = zone.npcs.find((n) => n.npcId === npcId);
      return p ? { x: p.x, z: p.z + 1.6 } : null;
    };
    let dest: { x: number; z: number } | null = null;
    if (prog.ready) dest = npcPos(def.turnInNpcId);
    else {
      const next = def.objectives.find((o) => {
        const st = prog.objectives.find((x) => x.id === o.id);
        return !st || st.current < st.required;
      });
      if (next?.type === 'talk') dest = npcPos(next.npcId);
      else if (next?.type === 'explore') {
        const a = zone.areas.find((x) => x.id === next.areaId);
        if (a) dest = { x: a.x, z: a.z + a.r * 0.6 };
      } else if (next?.type === 'kill') {
        const sp = zone.monsterSpawns.find((x) => x.monsterId === next.monsterId);
        if (sp) dest = { x: sp.x, z: sp.z + sp.radius + 2 };
      } else if (next?.type === 'collect') {
        const monster = Object.values(MONSTERS).find((m) =>
          LOOT_TABLES[m.lootTable]?.entries.some((e) => e.itemId === next.itemId),
        );
        const sp = monster ? zone.monsterSpawns.find((x) => x.monsterId === monster.id) : undefined;
        if (sp) dest = { x: sp.x, z: sp.z + sp.radius + 2 };
      }
    }
    if (dest && this.travelTo(dest.x, dest.z)) gameUi.toast('info', 'Auto-travelling… (move to cancel)');
  }

  get autoTravelling(): boolean {
    return this.autopath.length > 0;
  }

  selfPosition(): { x: number; z: number; facing: number } | null {
    return this.pred
      ? { x: this.pred.x, z: this.pred.z, facing: Math.atan2(this.pred.fx, this.pred.fz) }
      : null;
  }

  private updateInteractHint(): void {
    const it = this.interactables()[0];
    const key = it ? `${it.kind}:${it.id}` : '';
    if (key === this.interactKey) return;
    this.interactKey = key;
    useGame.setState({ interact: it ? { key: 'F', label: it.label } : null });
  }

  private pushHud(): void {
    const s = useGame.getState().self;
    if (!s || !this.pred) return;
    useGame.setState({
      self: {
        ...s,
        hp: this.hp,
        maxHp: this.maxHp,
        mp: this.mp,
        maxMp: this.maxMp,
        stamina: this.pred.stamina,
        dead: this.dead,
        deadAt: this.deadAt,
        statuses: this.statuses,
      },
    });
    this.syncTarget();
    const cb = useGame.getState().castBar;
    if (cb && performance.now() > cb.end + 150) useGame.setState({ castBar: null });
  }

  private syncTarget(): void {
    const v = this.targetId !== null ? this.entities.get(this.targetId) : undefined;
    let target: HudTarget | null = null;
    if (v && v.fading === null && v.dto.k !== 'loot') {
      target = {
        id: v.id,
        kind: v.dto.k as HudTarget['kind'],
        name: v.dto.name,
        ...(v.dto.level !== undefined ? { level: v.dto.level } : {}),
        hp: v.hp,
        maxHp: v.maxHp,
        statusBits: v.statusBits,
        dead: v.dead,
      };
    }
    const cur = useGame.getState().target;
    if (JSON.stringify(cur) !== JSON.stringify(target)) useGame.setState({ target });
  }

  private showError(text: string): void {
    const now = performance.now();
    if (now - this.lastErrorAt < 600) return;
    this.lastErrorAt = now;
    gameUi.toast('error', text);
    audio.play('ui_error', { volume: 0.6 });
  }

  private trackFps(dt: number): void {
    this.fpsFrames++;
    this.fpsTime += dt;
    if (this.fpsTime >= 1) {
      const fps = Math.round(this.fpsFrames / this.fpsTime);
      this.fpsFrames = 0;
      this.fpsTime = 0;
      useGame.setState({ fps });
      this.fpsHistory.push(fps);
      if (this.fpsHistory.length > 6) this.fpsHistory.shift();
      if (this.qualityAuto && this.fpsHistory.length === 6 && document.visibilityState === 'visible') {
        const avg = this.fpsHistory.reduce((a, b) => a + b, 0) / 6;
        if (avg < 32 && this.renderer.quality !== 'low') {
          this.applyQuality(this.renderer.quality === 'high' ? 'medium' : 'low');
          this.fpsHistory = [];
        }
      }
    }
  }

  private resize(): void {
    const r = this.opts.container.getBoundingClientRect();
    this.renderer.resize(r.width, r.height);
    this.cam.resize(r.width / Math.max(1, r.height));
    this.vfx.particles.setViewportHeight(r.height * this.renderer.renderer.getPixelRatio());
  }

  rendererInfo() {
    return {
      ...this.renderer.info(),
      quality: this.renderer.quality,
      entities: this.entities.size,
      effects: this.vfx.activeCount,
      pending: this.pending.length,
    };
  }

  private debugApi() {
    return {
      game: this,
      state: () => ({
        selfId: this.selfId,
        pos: this.pred ? { x: this.pred.x, z: this.pred.z } : null,
        hp: this.hp,
        mp: this.mp,
        dead: this.dead,
        target: this.targetId,
        connection: useGame.getState().connection,
        entities: [...this.entities.values()].map((v) => ({
          id: v.id,
          k: v.dto.k,
          name: v.dto.name,
          x: v.pos.x,
          z: v.pos.z,
          hp: v.hp,
          dead: v.dead,
          npcId: v.dto.npcId,
          monsterId: v.dto.monsterId,
        })),
        renderer: this.rendererInfo(),
      }),
      walkTo: (x: number, z: number) => this.travelTo(x, z),
      target: (id: number) => this.setTarget(id),
      press: (a: Action) => this.input.trigger(a),
    };
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.net.close();
    this.input.detach();
    this.resizeObserver.disconnect();
    this.unsubSettings();
    audio.stopMusic();
    audio.stopAmbience();
    for (const id of [...this.entities.keys()]) this.removeView(id);
    this.world?.dispose();
    this.vfx.dispose();
    this.overlay.dispose();
    this.renderer.dispose();
    this.canvas.remove();
    const w = window as unknown as { __jianghu?: unknown };
    if (w.__jianghu) delete w.__jianghu;
  }
}

const tmpV = new THREE.Vector3();

function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}
